import get from "lodash/get";
import has from "lodash/has";
import { store } from "../core";
import { API as LegacyAPI } from "../legacy/api";
import { types } from "../v2/redux/types";
import { IPlayUIAPI, IPlayUIState } from "./api";
import { IAPIEventMapping, mappings } from "./mappings";

const setState = (value: IPlayUIState) => {
  return {
    type: types.SET_STATE,
    value
  };
};

export class API implements IPlayUIAPI {
  // we don't have listeners for some actions so
  // we need to listen to them directly into the
  // emitter and dispatch to the new API callbacks
  // if any of those is defined
  // we are basically forwarding events to callbacks

  set onMoneyTypeChangeRequest(func) {
    LegacyAPI.off("moneytype.changed", this.onMoneyTypeChangeRequestCallback);
    this.onMoneyTypeChangeRequestCallback = func;
    LegacyAPI.on("moneytype.changed", this.onMoneyTypeChangeRequestCallback);
  }

  set onVolumeChangeRequest(func) {
    LegacyAPI.off("volume.changerequest", this.onVolumeChangeRequestCallback);
    this.onVolumeChangeRequestCallback = func;
    LegacyAPI.on("volume.changerequest", this.onVolumeChangeRequestCallback);
  }

  set onMuteChangeRequest(func) {
    LegacyAPI.off("mute.changerequest", this.onMuteChangeRequestCallback);
    this.onMuteChangeRequestCallback = func;
    LegacyAPI.on("mute.changerequest", this.onMuteChangeRequestCallback);
  }

  set onSpinRequest(func) {
    LegacyAPI.off("spinbutton.click", this.onSpinRequestCallback);
    LegacyAPI.off("autoplaystartbutton.click", this.onSpinRequestCallback);
    LegacyAPI.off("autoplaystopbutton.click", this.onSpinRequestCallback);
    this.onSpinRequestCallback = func;
    LegacyAPI.on("spinbutton.click", this.onSpinRequestCallback);
    LegacyAPI.on("autoplaystartbutton.click", this.onSpinRequestCallback);
    LegacyAPI.on("autoplaystopbutton.click", this.onSpinRequestCallback);
  }

  set onHomeRequest(func) {
    LegacyAPI.off("home.request", this.onHomeRequestCallback);
    this.onHomeRequestCallback = func;
    LegacyAPI.on("home.request", this.onHomeRequestCallback);
  }

  set onPluginRequest(func) {
    LegacyAPI.off("plugin.request", this.onPluginRequestCallback);
    this.onPluginRequestCallback = func;
    LegacyAPI.on("plugin.request", this.onPluginRequestCallback);
  }

  set onBetDenominationChangeRequest(func) {
    LegacyAPI.off(
      "betdenomination.changerequest",
      this.onBetDenominationChangeRequestCallback
    );
    this.onBetDenominationChangeRequestCallback = func;
    LegacyAPI.on(
      "betdenomination.changerequest",
      this.onBetDenominationChangeRequestCallback
    );
  }

  set onBetLevelChangeRequest(func) {
    LegacyAPI.off(
      "betlevel.changerequest",
      this.onBetLevelChangeRequestCallback
    );
    this.onBetLevelChangeRequestCallback = func;
    LegacyAPI.on(
      "betlevel.changerequest",
      this.onBetLevelChangeRequestCallback
    );
  }

  set onGameRulesRequest(func) {
    LegacyAPI.off("gamerulesbutton.click", this.onGameRulesRequestCallback);
    this.onGameRulesRequestCallback = func;
    LegacyAPI.on("gamerulesbutton.click", this.onGameRulesRequestCallback);
  }

  set onGameHistoryRequest(func) {
    LegacyAPI.off("gamehistorybutton.click", this.onGameHistoryRequestCallback);
    this.onGameHistoryRequestCallback = func;
    LegacyAPI.on("gamehistorybutton.click", this.onGameHistoryRequestCallback);
  }

  set onAutoplaySelectRoundsChangeRequest(func) {
    LegacyAPI.off(
      "autoplay.selectedrounds.changerequest",
      this.onAutoplaySelectRoundsChangeRequestCallback
    );
    this.onAutoplaySelectRoundsChangeRequestCallback = func;
    LegacyAPI.on(
      "autoplay.selectedrounds.changerequest",
      this.onAutoplaySelectRoundsChangeRequestCallback
    );
  }

  set onViewChangeRequest(func) {
    LegacyAPI.off("view.changerequest", this.onViewChangeRequestCallback);
    this.onViewChangeRequestCallback = func;
    LegacyAPI.on("view.changerequest", this.onViewChangeRequestCallback);
  }

  set onTickerAnimationEnd(func) {
    LegacyAPI.off("ticker.animation.end", this.onTickerAnimationEndCallback);
    this.onTickerAnimationEndCallback = func;
    LegacyAPI.on("ticker.animation.end", this.onTickerAnimationEndCallback);
  }

  set onAutoplaySettingChangeRequest(func) {
    LegacyAPI.off(
      "autoplay.settings.changerequest",
      this.onAutoplaySettingChangeRequestCallback
    );
    this.onAutoplaySettingChangeRequestCallback = func;
    LegacyAPI.on(
      "autoplay.settings.changerequest",
      this.onAutoplaySettingChangeRequestCallback
    );
  }

  set onAutoplayResetRequest(func) {
    LegacyAPI.off(
      "autoplay.settings.reset",
      this.onAutoplayResetRequestCallback
    );
    this.onAutoplayResetRequestCallback = func;
    LegacyAPI.on(
      "autoplay.settings.reset",
      this.onAutoplayResetRequestCallback
    );
  }

  set onQuickSpinChangeRequest(func) {
    LegacyAPI.off(
      "quickspin.changerequest",
      this.onQuickSpinChangeRequestCallback
    );
    this.onQuickSpinChangeRequestCallback = func;
    LegacyAPI.on(
      "quickspin.changerequest",
      this.onQuickSpinChangeRequestCallback
    );
  }

  set onSettingChangeRequest(func) {
    LegacyAPI.off("setting.changerequest", this.onSettingChangeRequestCallback);
    this.onSettingChangeRequestCallback = func;
    LegacyAPI.on("setting.changerequest", this.onSettingChangeRequestCallback);
  }

  private onMoneyTypeChangeRequestCallback;
  private onVolumeChangeRequestCallback;
  private onMuteChangeRequestCallback;
  private onSpinRequestCallback;
  private onHomeRequestCallback;
  private onPluginRequestCallback;
  private onBetDenominationChangeRequestCallback;
  private onBetLevelChangeRequestCallback;
  private onGameRulesRequestCallback;
  private onGameHistoryRequestCallback;
  private onAutoplaySelectRoundsChangeRequestCallback;
  private onViewChangeRequestCallback;
  private onTickerAnimationEndCallback;
  private onAutoplaySettingChangeRequestCallback;
  private onAutoplayResetRequestCallback;
  private onQuickSpinChangeRequestCallback;
  private onSettingChangeRequestCallback;

  public setState(state: IPlayUIState) {
    mappings.forEach(mapping => emitIf(state, mapping));
    store.dispatch(setState(state));
  }
}

const emitIf = (state: IPlayUIState, mapping: IAPIEventMapping) => {
  const { event, property, transform, pre, post, guard } = mapping;

  if (has(state, property)) {
    const unmodified = get(state, property);
    const shouldEmit = guard ? guard(unmodified) : true;
    const value = transform ? transform(unmodified) : unmodified;

    if (shouldEmit) {
      if (pre) {
        pre(value);
      }

      LegacyAPI.emit(event, value);

      if (post) {
        post(value);
      }
    }
  }
};
